import React from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock } from 'react-icons/fa';

const MapSection: React.FC<{ language: 'ar' | 'en' }> = ({ language }) => {
  const isRTL = language === 'ar';
  return (
    <section className={`section-padding bg-gray-50 ${isRTL ? 'font-cairo' : 'font-montserrat'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
            {language === 'ar' ? 'موقعنا' : 'Our Location'}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {language === 'ar' ? 'زورنا في' : 'Visit Us at'}
            <span className="text-gradient"> {language === 'ar' ? 'مقرنا الرئيسي' : 'Our Main Office'}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {language === 'ar'
              ? 'نحن موجودون في قلب القاهرة، جاهزون لاستقبالكم وتقديم أفضل الخدمات.'
              : 'We are located in the heart of Cairo, ready to welcome you and provide the best services.'}
          </p>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Map */}
          <div className="relative">
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
              <div className="w-full h-96 md:h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3453.7!2d31.2357!3d30.0444!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzDCsDAyJzM5LjgiTiAzMcKwMTQnMDguNSJF!5e0!3m2!1sen!2seg!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={language === 'ar' ? 'موقع شركة مكة البركة للمقاولات' : 'Mecca El Baraka For Construction Location'}
                ></iframe>
              </div>
            </div>
            {/* Floating Info Card */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <FaMapMarkerAlt className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">{language === 'ar' ? 'المقر الرئيسي' : 'Main Office'}</h4>
                  <p className="text-sm text-gray-600">38 الملتقي العربي - شيراتون - مصر</p>
                </div>
              </div>
            </div>
          </div>
          {/* Contact Information */}
          <div className="space-y-8">
            {/* Office Hours */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center space-x-4 rtl:space-x-reverse mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <FaClock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{language === 'ar' ? 'ساعات العمل' : 'Working Hours'}</h3>
                  <p className="text-gray-600">{language === 'ar' ? 'نحن متاحون لخدمتك' : 'We are available to serve you'}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">{language === 'ar' ? 'الأحد - الخميس' : 'Sunday - Thursday'}</span>
                  <span className="font-semibold text-gray-900">8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">{language === 'ar' ? 'الجمعة' : 'Friday'}</span>
                  <span className="font-semibold text-gray-900">9:00 AM - 2:00 PM</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">{language === 'ar' ? 'السبت' : 'Saturday'}</span>
                  <span className="font-semibold text-gray-900">{language === 'ar' ? 'مغلق' : 'Closed'}</span>
                </div>
              </div>
            </div>
            {/* Contact Details */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-6">{language === 'ar' ? 'معلومات الاتصال' : 'Contact Information'}</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaPhone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{language === 'ar' ? 'الهاتف' : 'Phone'}</h4>
                    <a href="tel:020224181844" className="text-blue-600 hover:text-blue-700 transition-colors">
                      020224181844
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaEnvelope className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</h4>
                    <a href="mailto:info@meccaelbaraka.com" className="text-blue-600 hover:text-blue-700 transition-colors">
                      info@meccaelbaraka.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FaMapMarkerAlt className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{language === 'ar' ? 'العنوان' : 'Address'}</h4>
                    <p className="text-gray-600">
                      38 الملتقي العربي - شيراتون<br />
                      {language === 'ar' ? 'القاهرة، مصر' : 'Cairo, Egypt'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Emergency Contact */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-4">{language === 'ar' ? 'اتصال طوارئ' : 'Emergency Contact'}</h3>
              <p className="text-blue-100 mb-4">
                {language === 'ar'
                  ? 'للاستفسارات العاجلة والمشاريع الكبيرة، يمكنك الاتصال بنا على مدار الساعة'
                  : 'For urgent inquiries and large projects, you can contact us 24/7'}
              </p>
              <a
                href="tel:020224181844"
                className="inline-flex items-center space-x-2 rtl:space-x-reverse bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                <FaPhone className="w-4 h-4" />
                <span>{language === 'ar' ? 'اتصل الآن' : 'Call Now'}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MapSection; 